import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BaseButton } from '@verifier-user/verifier-headless-ui'
import { ChevronDoubleRightIcon } from '@heroicons/react/24/solid'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid'
import { useApplicationContext } from '../../contexts/appContext'

export const EastVenturesIntro = ({
  currentErrorRef: currentErrorRef,
  setProcessing: setProcessing,
  setError: setError,
  setErrorShown: setErrorShown
}) => {
  const [errorMessage, setErrorMessage] = useState()

  const [abn, setAbn] = useState('')
  const [name, setName] = useState('')
  const [position, setPosition] = useState('')

  const [businessName, setBusinessName] = useState()
  const containerRef = useRef(null)

  const { verifyAbn, vendorData } = useApplicationContext()

  const navigate = useNavigate()

  const isValid = () => {
    let canProceed = true
    let message = []
    if (!abn) {
      message.push('ABN not specified')
      canProceed = false
    }

    if (!name) {
      message.push('Name not specified')
      canProceed = false
    }

    if (!position) {
      message.push('Position not specified')
      canProceed = false
    }

    if (!businessName) {
      message.push('ABN not confirmed')
      canProceed = false
    }

    if (!canProceed) {
      setErrorMessage(message)
    }

    if (canProceed) {
      navigate('/welcome')
    }
  }

  const verify = (event) => {
    event.preventDefault()

    if (abn) {
      setProcessing(true)
      verifyAbn({
        abn,
        name: name,
        position: position,
        onSuccess: (name) => {
          setProcessing(false)
          setErrorMessage(undefined)
          setBusinessName(name)
        },
        onError: (error) => {
          setProcessing(false)
          setError(error)
          if (error.status === 404) {
            setErrorMessage([error.data])
          } else {
            currentErrorRef.current = {
              title: 'ABN Lookup service Unavailable An error has occurred',
              retryFunction: () => verify(event),
              cancelFunction: () => setErrorShown(false),
              okTitle: 'Try Again',
              cancelTitle: 'Cancel',
              showContent: true,
              errorContent: (
                <div>
                  <p>
                    We're sorry but there has been a connection issue. Please try again, and if the issue persists,
                    start the process again.
                  </p>
                </div>
              )
            }
            setErrorShown(true)
          }
        }
      })
    } else {
      let message = []
      if (!abn) {
        message.push('ABN not specified')
      }
      setErrorMessage(message)
    }
  }

  const handleChangeAbn = (event) => {
    setBusinessName(undefined)
    setErrorMessage(undefined)
    setAbn(event.target.value)
  }

  const handleNameChange = (event) => {
    setBusinessName(undefined)
    setErrorMessage(undefined)
    setName(event.target.value)
  }

  const handlePositionChange = (event) => {
    setBusinessName(undefined)
    setErrorMessage(undefined)
    setPosition(event.target.value)
  }

  const getAcnOrAbn = () => {
    if (vendorData.config.acn) {
      return `ACN ${vendorData.config.acn}`
    } else if (vendorData.config.abn) {
      return `ABN ${vendorData.config.abn}`
    } else {
      return ''
    }
  }

  const stripProtocol = (url) => {
    return url.replace(/(^\w+:|^)\/\//, '')
  }

  return (
    <main>
      <div ref={containerRef} className={'font-body'}>
        <h1 data-testid='welcome-heading' className={'text-4xl sm:text-5xl mb-2 leading-tight'}>
          Letter Of Authority
        </h1>
        <p>
          <span className='text-base sm:text-lg'>
            We authorise {vendorData.config.corporateName} {getAcnOrAbn()}{' '}
            {vendorData.config.corporateAbbreviation && `(${vendorData.config.corporateAbbreviation})`} to:
          </span>
        </p>
        <div className={'mx-10'}>
          <p>
            <span className='text-base sm:text-lg'>
              - Act on our behalf to seek electricity and gas pricing and proposals from licenced energy market{' '}
              participants;
            </span>
          </p>
          <p>
            <span className='text-base sm:text-lg'>- Access where appropriate relating to our accounts:</span>
          </p>
          <div className={'mx-10 pb-4'}>
            <ol>
              <li>historical billing data, including annual electricity consumption data;</li>
              <li>metering information and tariff details;</li>
              <li>start and end contract dates.</li>
            </ol>
          </div>
          <p>
            <span className='text-base sm:text-lg'>
              - Where {vendorData.config.corporateName} is successful in procuring contract agreement for our business,
              where possible, receive a duplicate copy of our bill(s) for verification and administrative purposes
              (applicable for the chosen contract period only).
            </span>
          </p>
        </div>
        <p>
          <span className='text-base sm:text-lg'>
            We agree that all information provided is true and correct and authorise the release of our relevant company
            details to licenced energy market participants.
          </span>
        </p>
        <p>
          <span className='text-base sm:text-lg'>
            We acknowledge that our information of a private and confidential nature will be handled by{' '}
            {vendorData.config.corporateAbbreviation
              ? `${vendorData.config.corporateAbbreviation}`
              : `${vendorData.config.corporateName}`}{' '}
            in accordance with{' '}
            {vendorData.config.corporateAbbreviation
              ? `${vendorData.config.corporateAbbreviation}'s`
              : `${vendorData.config.corporateName}'s`}{' '}
            privacy policy, a copy of which can be obtained from{' '}
            <a href={`${vendorData.config.privacyPolicy}`} target={'_blank'} rel='noreferrer'>
              {stripProtocol(vendorData.config.privacyPolicy)}.
            </a>{' '}
            <ArrowTopRightOnSquareIcon className='w-4 h-4 text-infoCdrColor inline flex-none' />
          </span>
        </p>
        <p>
          <span className='text-base sm:text-lg'>
            We acknowledge that{' '}
            {vendorData.config.corporateAbbreviation
              ? `${vendorData.config.corporateAbbreviation}`
              : `${vendorData.config.corporateName}`}{' '}
            will not be offering us energy advice or pre-sale support and that{' '}
            {vendorData.config.corporateAbbreviation
              ? `${vendorData.config.corporateAbbreviation}`
              : `${vendorData.config.corporateName}`}{' '}
            may earn a commission from a licenced energy market participant for procuring a contract agreement for us.
          </span>
        </p>
        <p>
          <span className='text-base sm:text-lg'>This authority is valid for twelve (12) months.</span>
        </p>
        <p>
          <span className='text-base sm:text-lg'>Please enter the details specified in the form below.</span>
        </p>
        <form onSubmit={verify}>
          <div className='form-container gap-3'>
            <label htmlFor='name'>I, </label>
            <input
              type='text'
              id='name'
              name='name'
              value={name}
              onChange={handleNameChange}
              className='underline-input'
              placeholder='Name'
            />
            <label htmlFor='position'>,</label>
            <input
              type='text'
              id='position'
              value={position}
              onChange={handlePositionChange}
              name='position'
              className='underline-input'
              placeholder='Position'
            />
          </div>
          <div className={'flex gap-3 mt-2'}>
            declare that I am authorised to sign this authority on behalf of the entity outlined below.
          </div>
          <div>
            <input
              type='text'
              id='abn'
              name='abn'
              value={abn}
              onChange={handleChangeAbn}
              className={'underline-input abn mr-2 mt-2'}
              placeholder={'ABN'}
            />
            <BaseButton data-testid='confirm-abn' className={'mb-4'} onClick={verify} id='eastVentures-continue-button'>
              Confirm ABN
            </BaseButton>
          </div>

          <div>
            {errorMessage && (
              <div className={'mt-2 mb-4'}>
                <span className='verifier-error-text text-red-600 mt-2 mb-0' data-testid='login-error'>
                  <ul>
                    {errorMessage.map((msg, index) => (
                      <li key={index}>{msg}</li>
                    ))}
                  </ul>
                </span>
              </div>
            )}

            {businessName && (
              <div className='mt-2 mb-4'>
                If <strong>{businessName}</strong> is your business name, click continue.
              </div>
            )}
          </div>

          <div className={'mt-2'}>
            <BaseButton
              data-testid='eastVentures-continue-button'
              className={'mb-4'}
              onClick={isValid}
              id='eastVentures-continue-button'
            >
              Continue
              <ChevronDoubleRightIcon className='w-4 h-4 inline' />
            </BaseButton>
          </div>
        </form>
      </div>
    </main>
  )
}
